class ResponseError extends Error {
    status;
    body;
    constructor(name, status, body) {
        super(`Server responded with status: ${status} and body: "${body}"`);
        this.name = name;
        this.status = status;
        this.body = body;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}
export class UnknownResponseError extends ResponseError {
    constructor(status, body) {
        super('UnknownResponseError', status, body);
    }
}
export class UnauthorizedResponseError extends ResponseError {
    serverMessage;
    errorCode;
    /** @deprecated Use errorCode instead */
    code;
    attemptsRemaining;
    isBlocked;
    /** Number of failed sign-in attempts so far (from signin_failed_count in response) */
    signinFailedCount;
    constructor(body, status = 401) {
        super('UnauthorizedResponseError', status, body);
        try {
            const parsed = JSON.parse(body);
            this.errorCode = parsed.errorCode ?? parsed.code ?? 'UNAUTHORIZED';
            this.code = this.errorCode;
            this.serverMessage = parsed.message ?? 'Invalid credentials';
            this.attemptsRemaining = parsed.attemptsRemaining ?? null;
            this.isBlocked = parsed.isBlocked ?? false;
            this.signinFailedCount = parsed.signin_failed_count ?? null;
        }
        catch {
            this.errorCode = 'UNAUTHORIZED';
            this.code = 'UNAUTHORIZED';
            this.serverMessage = body || 'Invalid credentials';
            this.attemptsRemaining = null;
            this.isBlocked = false;
            this.signinFailedCount = null;
        }
    }
}
export class ForbiddenResponseError extends ResponseError {
    errorCode;
    constructor(body, status = 403) {
        super('ForbiddenResponseError', status, body);
        try {
            const parsed = JSON.parse(body);
            this.errorCode = parsed.errorCode ?? parsed.code ?? 'FORBIDDEN';
        }
        catch {
            this.errorCode = 'FORBIDDEN';
        }
    }
}
export class NotFoundResponseError extends ResponseError {
    errorCode;
    constructor(body, status = 404) {
        super('NotFoundResponseError', status, body);
        try {
            const parsed = JSON.parse(body);
            this.errorCode = parsed.errorCode ?? parsed.code ?? 'NOT_FOUND';
        }
        catch {
            this.errorCode = 'NOT_FOUND';
        }
    }
}
/**
 * Carries a machine-readable `errorCode` and optional field-level errors
 * for any non-2xx response that isn't a 401/403/404.
 */
export class ApiResponseError extends ResponseError {
    errorCode;
    fieldErrors;
    constructor(status, body) {
        super('ApiResponseError', status, body);
        try {
            const parsed = JSON.parse(body);
            this.errorCode = parsed.errorCode ?? parsed.code ?? 'UNKNOWN_ERROR';
            this.fieldErrors = parsed.fieldErrors;
        }
        catch {
            this.errorCode = 'UNKNOWN_ERROR';
            this.fieldErrors = undefined;
        }
    }
}
export const toResponseError = (status, body) => {
    if (status === 401)
        return new UnauthorizedResponseError(body);
    if (status === 403)
        return new ForbiddenResponseError(body);
    if (status === 404)
        return new NotFoundResponseError(body);
    return new ApiResponseError(status, body);
};
/**
 * Extracts the machine-readable errorCode from any API error thrown by toResponseError.
 * Returns an empty string for non-API errors (network failures, etc.).
 */
export function getErrorCode(error) {
    if (error instanceof UnauthorizedResponseError ||
        error instanceof ForbiddenResponseError ||
        error instanceof NotFoundResponseError ||
        error instanceof ApiResponseError) {
        return error.errorCode;
    }
    return '';
}
